import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
} from "@devhop/ui";
import { useMemo, useState } from "react";
import type { AdminCatalogItem } from "@/modules/cf-feed/presentation/api/types";
import {
  type PriceInput,
  priceForWorkspace,
  resolvePriceInput,
  type WorkspacePromo,
} from "../../domain/contracts/live-workspace";
import { kip, WorkspaceSelect } from "./WorkspaceProductEditor";

const neutralPromo: WorkspacePromo = {
  listDiscountPercent: null,
  listDiscountFixedLak: null,
  freeShipping: false,
  shippingFeeLak: null,
  options: [],
};

type PreviewItem = {
  item: AdminCatalogItem;
  after: number;
  error?: string;
};

function automaticOptionSurcharge(promo: WorkspacePromo) {
  return promo.options
    .filter((option) => option.isAutoAttach)
    .reduce((sum, option) => sum + option.surchargeLak, 0);
}

function priceError(error: unknown) {
  const code = error instanceof Error ? error.message : "INVALID_PRICE";
  if (code === "VARIANT_NOT_AVAILABLE") return "ສິນຄ້າບໍ່ພ້ອມໃຊ້ງານ";
  if (code === "PRICE_NOT_REPRESENTABLE") return "ສ່ວນຫຼຸດຕ້ອງເປັນຫຼັກພັນ";
  if (code === "INVALID_DISCOUNT") return "ສ່ວນຫຼຸດ % ບໍ່ຖືກຕ້ອງ";
  return "ລາຄາບໍ່ຖືກຕ້ອງ";
}

export function WorkspaceCatalogPriceDialog({
  items,
  initialPrice,
  promo,
  busy,
  onClose,
  onConfirm,
}: {
  items: AdminCatalogItem[];
  initialPrice: PriceInput;
  promo?: WorkspacePromo;
  busy: boolean;
  onClose: () => void;
  onConfirm: (price: PriceInput) => void | Promise<void>;
}) {
  const [price, setPrice] = useState(initialPrice);
  const [preview, setPreview] = useState<{
    fingerprint: string;
    items: PreviewItem[];
  } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const effectivePromo = promo ?? neutralPromo;
  const optionSurcharge = automaticOptionSurcharge(effectivePromo);
  const fingerprint = useMemo(
    () =>
      JSON.stringify([
        price,
        items.map((item) => [item.variantId, Number(item.price)]),
        effectivePromo,
      ]),
    [effectivePromo, items, price],
  );
  const previewItems =
    preview?.fingerprint === fingerprint ? preview.items : null;
  const calculate = () => {
    setPreviewing(true);
    const next = items.map((item): PreviewItem => {
      try {
        if (item.isAvailable === false)
          throw new Error("VARIANT_NOT_AVAILABLE");
        const discount = resolvePriceInput(Number(item.price), price);
        return {
          item,
          after: priceForWorkspace(
            Number(item.price),
            discount,
            effectivePromo,
          ),
        };
      } catch (error) {
        return {
          item,
          after: Number(item.price),
          error: priceError(error),
        };
      }
    });
    setPreview({ fingerprint, items: next });
    setPreviewing(false);
  };
  const hasError = previewItems?.some((item) => item.error) ?? false;
  const suggestedSale = (item: AdminCatalogItem) => {
    const base = Number(item.price);
    return Math.max(
      0,
      Math.min(
        base,
        base - Math.round((base - (price.value ?? 0)) / 1000) * 1000,
      ),
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>ຕັ້ງຄ່າ {items.length} ລາຍການ</DialogTitle>
          <DialogDescription>ກວດລາຄາຈິງ ແລະຄວາມເຂົ້າກັນໄດ້ກ່ອນເພີ່ມ</DialogDescription>
        </DialogHeader>
        <div className="grid gap-5 sm:grid-cols-2">
          <section className="space-y-3">
            <h3 className="font-semibold">ລາຄາ ແລະສ່ວນຫຼຸດ</h3>
            <WorkspaceSelect
              label="ວິທີຕັ້ງລາຄາ"
              value={price.mode}
              onChange={(mode) =>
                setPrice({
                  mode: mode as PriceInput["mode"],
                  value: undefined,
                })
              }
              items={[
                ["sale", "ກອກລາຄາຂາຍຈິງ"],
                ["inherit", "ໃຊ້ລາຄາ / ໂປຣຂອງງານ"],
                ["none", "ບໍ່ຫຼຸດລາຄາ"],
                ["percent", "ຫຼຸດ %"],
                ["fixed", "ຫຼຸດເປັນກີບ"],
              ]}
            />
            {price.mode !== "inherit" && price.mode !== "none" ? (
              <label htmlFor="catalog-price-value" className="block space-y-1">
                <span className="text-sm">
                  {price.mode === "sale"
                    ? "ລາຄາຂາຍຈິງ (ກີບ)"
                    : price.mode === "percent"
                      ? "ສ່ວນຫຼຸດ (%)"
                      : "ສ່ວນຫຼຸດ (ກີບ)"}
                </span>
                <Input
                  id="catalog-price-value"
                  type="number"
                  min={0}
                  max={price.mode === "percent" ? 100 : 999999999}
                  className="min-h-11"
                  value={price.value ?? ""}
                  onChange={(event) =>
                    setPrice({
                      ...price,
                      value:
                        event.target.value === ""
                          ? undefined
                          : Number(event.target.value),
                    })
                  }
                />
              </label>
            ) : null}
          </section>
          <section className="space-y-3">
            <h3 className="font-semibold">ບໍລິການເສີມ</h3>
            <p className="text-muted-foreground text-sm">
              {promo
                ? `ໃຊ້ບໍລິການເລີ່ມຕົ້ນຈາກງານນີ້ ${kip(optionSurcharge)} / ຊິ້ນ`
                : "ຊຸດສິນຄ້າບໍ່ເກັບບໍລິການເສີມ. ລະບົບຈະໃຊ້ຄ່າຈາກ Feed ຫຼື Live ຕອນນຳຊຸດໄປໃຊ້."}
            </p>
          </section>
        </div>
        {previewItems ? (
          <div className="space-y-2 rounded-lg border p-3">
            <p className="font-semibold">ກວດກາກ່ອນເພີ່ມ</p>
            <div className="max-h-64 overflow-auto">
              {previewItems.map(({ item, after, error }) => (
                <div key={item.variantId} className="border-b py-2 text-sm">
                  <strong>
                    {item.defaultCfCode ?? item.suggestedCfCode ?? item.sku}
                  </strong>{" "}
                  · {kip(Number(item.price))} → {kip(after)} +{" "}
                  {kip(optionSurcharge)} ={" "}
                  <strong>{kip(after + optionSurcharge)} / ຊິ້ນ</strong>
                  {error ? (
                    <div className="mt-1 text-destructive">
                      <p>{error}</p>
                      {price.mode === "sale" && price.value !== undefined ? (
                        <Button
                          type="button"
                          variant="outline"
                          className="mt-2 min-h-11"
                          onClick={() =>
                            setPrice({
                              mode: "sale",
                              value: suggestedSale(item),
                            })
                          }
                        >
                          ໃຊ້ລາຄາ {kip(suggestedSale(item))}
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
            <p className="text-muted-foreground text-sm">
              ຄ່າຈັດສົ່ງແຍກຈາກຍອດຕໍ່ຊິ້ນ · ຕອນນຳໄປໃຊ້ຈະກວດສະຕັອກ ແລະສະຖານະອີກຄັ້ງ
            </p>
          </div>
        ) : null}
        <div className="sticky bottom-0 flex flex-wrap justify-end gap-2 bg-background py-2">
          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            disabled={busy}
            onClick={onClose}
          >
            ຍົກເລີກ
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="min-h-11"
            disabled={
              previewing ||
              busy ||
              (!["inherit", "none"].includes(price.mode) &&
                price.value === undefined)
            }
            onClick={calculate}
          >
            {previewing ? "ກຳລັງກວດ…" : "ກວດລາຄາກ່ອນ"}
          </Button>
          <Button
            type="button"
            className="min-h-11"
            disabled={
              busy || !previewItems || hasError || previewItems.length === 0
            }
            onClick={() => void onConfirm(price)}
          >
            {busy ? "ກຳລັງເພີ່ມ…" : "ຢືນຢັນລາຍການທີ່ໃຊ້ໄດ້"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
